'use client';

import { c as _c } from "react/compiler-runtime";
import { Fragment, type ReactNode, useLayoutEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowRight, ChevronRight, Clock3, Compass, Lock, Sparkles, Users } from 'lucide-react';
import { useLabsStore, usePack } from '@/app/(platform)/(subscription-gated)/labs/_engine/pack-context';
import { BrandButton, MetaTag, PillTag } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/_components';
import { StepShell } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/step-shell';
import { StepCard } from '@/app/(platform)/(subscription-gated)/labs/_engine/screens/welcome';
import { isFieldLessPack } from '../_engine/field-less-pack';
import { guideAudienceName } from '../_engine/guide-audience';
import { usePreviewAnalytics } from '../_engine/preview-analytics';

/**
 * The public preview's front door.
 *
 * 🔴 WHY THIS IS NOT THE SPRINT'S WELCOME SCREEN. That screen makes three promises the preview does
 * not keep, and all three live in the pack's shared `copy.welcome`:
 *
 *   1. "Spend 15 minutes finding out." The preview is about five.
 *   2. "About 15 minutes" on the meta row. Same claim, second place.
 *   3. Step 3, "Analyze a real company / product / business". True of the sprint's M3 and M4; at the
 *      time this screen was written the preview ran neither.
 *
 * ⚠️ POINT 3 IS NOW STALE AND THE CARD UNDER-PROMISES. M3 was added back to `PREVIEW_STEPS` on
 * 2026-08-26, so the preview DOES analyse something: a reader reads a real operating table and computes
 * three rates. This screen still advertises two modules and a result. That is the safe direction to be
 * wrong in (the docblock exists to stop the preview over-promising) but it is wrong: the third card
 * could name the actual work AND the result, on the screen with the highest drop-off in the flow.
 * Deliberately left alone rather than fixed in passing, because it changes the front door of three
 * labs that are live.
 *
 * Reusing it meant opening by advertising the product this is a cut of, which is the one thing a
 * five-minute funnel cannot afford. The reader who came for a fifteen-minute analysis is exactly the
 * reader who leaves disappointed at minute four.
 *
 * WHAT COMES FROM THE PACK, AND WHAT DOES NOT. The vertical still names itself (`eyebrow`) and still
 * asks its own question (`headlineLead`), because those are the domain and they are as true here as in
 * the sprint. Everything about length and shape belongs to the surface, not the vertical, so it lives
 * here and reads identically across all three packs. The first two step cards are reused word for
 * word, since they describe the two modules the preview actually runs. Only the third is replaced.
 *
 * If a vertical ever needs its own preview wording, that is the moment to add a `previewWelcome` block
 * to `LabsCopy`, and not before. Three identical strings do not need three authored copies.
 */

/** Icons for the three cards, zipped by index. Card 3 is the result here, not an analysis. */
const STEP_ICONS: readonly LucideIcon[] = [Users, Compass, Sparkles];

/** The surface's own length claim. Measured: welcome to the archetype reveal is 202s, plus the payoff. */
const PREVIEW_HEADLINE_TAIL = 'Spend 5 minutes finding out.';
const PREVIEW_META_DURATION = 'About 5 minutes';

/** Names the pill for what this is. The sprint's own label is "Discovery experience". */
const PREVIEW_PILL_SUFFIX = '· 5-minute preview';

/**
 * The line under the headline. Two versions, because "career paths" is a claim, not a phrasing.
 *
 * The vertical labs genuinely contrast two paths WITHIN a field: FP&A against investing, strategy
 * against operations. Career Explorer contrasts two halves present in almost any job, and it is placed
 * on nursing and marketing and engineering pages. Telling that reader they are choosing between two
 * career paths would promise a choice the lab does not offer, on the screen with the highest drop-off
 * in the flow. See `field-less-pack.ts`.
 */
const PREVIEW_LEAD = 'Two real career paths. See which one is you.';
const FIELD_LESS_PREVIEW_LEAD = 'Two halves to almost every job. See which one is you.';

/** What the preview actually ends on, replacing the sprint's "Analyze a real ..." card. */
const PREVIEW_FINAL_STEP = {
  sub: 'Plus the externships that fit how you think',
  title: 'Get your result'
};

/**
 * The headline for a reader we can name a subject for, replacing the pack's own question.
 *
 * 🔴 IT DESCRIBES THE READER, NOT THE LAB. "Reading about Deloitte?" is a true statement about what
 * they were doing ten seconds ago on the page that linked here. "Deloitte's analysts work like this"
 * would be a claim about the content, and it would be false: this is the generic finance lab. The
 * distinction is the whole reason `guide-audience.ts` refuses topic slugs rather than guessing.
 *
 * The tail replaces "Spend 5 minutes finding out." because "Reading about X? Spend 5 minutes finding
 * out." never says what is being found out. The five-minute claim is not lost — it is on the meta row
 * below, which is where a reader looks for it anyway.
 */
const personalisedHeadlineLead = (audience: string): string => `Reading about ${audience}?`;
const PERSONALISED_HEADLINE_TAIL = 'Find out what that work is really like.';
export function PreviewWelcome() {
  const $ = _c(32);
  const {
    copy,
    slug
  } = usePack();
  const next = useLabsStore(_temp);
  const {
    guideSource
  } = usePreviewAnalytics();
  const [audience, setAudience] = useState(null);
  let t0;
  let t1;
  if ($[0] !== guideSource) {
    t0 = () => {
      setAudience(guideAudienceName(guideSource));
    };
    t1 = [guideSource];
    $[0] = guideSource;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
  let t2;
  if ($[3] !== audience || $[4] !== copy.welcome.headlineLead) {
    t2 = audience ? personalisedHeadlineLead(audience) : copy.welcome.headlineLead;
    $[3] = audience;
    $[4] = copy.welcome.headlineLead;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const headlineLead = t2;
  const headlineTail = audience ? PERSONALISED_HEADLINE_TAIL : PREVIEW_HEADLINE_TAIL;
  let t3;
  if ($[6] !== copy.welcome.steps) {
    t3 = [...copy.welcome.steps.slice(0, 2), PREVIEW_FINAL_STEP];
    $[6] = copy.welcome.steps;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const steps = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="opacity-70">{PREVIEW_PILL_SUFFIX}</span>;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== copy.welcome.eyebrow) {
    t5 = <PillTag>{copy.welcome.eyebrow} {t4}</PillTag>;
    $[9] = copy.welcome.eyebrow;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== headlineTail) {
    t6 = <span className="text-fs-text-subtle">{headlineTail}</span>;
    $[11] = headlineTail;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== headlineLead || $[14] !== t6) {
    t7 = <h1 className="font-instrument text-fs-display-sm text-fs-text sm:text-fs-display-md">{headlineLead} {t6}</h1>;
    $[13] = headlineLead;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = isFieldLessPack(slug) ? FIELD_LESS_PREVIEW_LEAD : PREVIEW_LEAD;
  let t9;
  if ($[16] !== t8) {
    t9 = <p className="font-medium text-fs-text-subtle text-fs-title-lg tracking-[-1px]">{t8}</p>;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t9) {
    t10 = <div className="flex flex-col items-center gap-2 sm:gap-4">{t7}{t9}</div>;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== steps) {
    t11 = <ol className="flex w-full flex-col items-stretch gap-2 text-left sm:flex-row sm:items-stretch sm:gap-0">{steps.map((step, i) => <Fragment key={step.title}><li className="flex sm:flex-1"><StepCard icon={STEP_ICONS[i] ?? Users} n={i + 1} sub={step.sub} title={step.title} /></li>{i < steps.length - 1 ? <li aria-hidden={true} className="hidden items-center justify-center px-2 text-fs-icon-subtle sm:flex"><ChevronRight className="h-4 w-4" strokeWidth={2} /></li> : null}</Fragment>)}</ol>;
    $[21] = steps;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:gap-4"><MetaTag icon={Clock3}>{PREVIEW_META_DURATION}</MetaTag><MetaTag icon={Lock}>No account needed</MetaTag></div>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== copy.welcome.cta || $[25] !== next) {
    t13 = <BrandButton icon={ArrowRight} onClick={next}>{copy.welcome.cta}</BrandButton>;
    $[24] = copy.welcome.cta;
    $[25] = next;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t13 || $[30] !== t5) {
    t14 = <StepShell showProgress={false}><div className="flex flex-col items-center gap-4 text-center sm:gap-8">{t5}{t10}{t11}{t12}{t13}</div></StepShell>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t13;
    $[30] = t5;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}
function _temp(s) {
  return s.next;
}